import React, { useEffect, useState } from 'react'
import { useTranslation } from 'next-i18next'
import cn from 'classnames'
import s from './styles/StoreSearch.module.scss'
import { Button } from '@corratech/pylot-ui'
import { Cross } from '@components/icons'
import CurrentLocation from '@components/icons/CurrentLocation'
import { Store } from './StoreList'
import { getPreciseDistance } from 'geolib'
import { ToastType, useUI } from '@corratech/pylot-ui/context'

interface StoreSearchProps {
    onSearch: (searchTerm: string) => void
    storeList: Store[]
    searchCount: number
    setFilteredStores: React.Dispatch<React.SetStateAction<Store[]>>
    setActiveStore: (storeId: number | null) => void
    onStoreTypeFilterChange: (value: number) => void
    storeTypeFilter: number[]
    setCurrentPosition: React.Dispatch<
        React.SetStateAction<google.maps.LatLngLiteral | null>
    >
    currentPosition: google.maps.LatLngLiteral | null
    setMapCenter: React.Dispatch<
        React.SetStateAction<google.maps.LatLngLiteral>
    >
    setZoom: React.Dispatch<React.SetStateAction<number>>
    setIsUserGeolocation: React.Dispatch<React.SetStateAction<boolean>>
}

export const StoreSearch: React.FC<StoreSearchProps> = ({
    onSearch,
    onStoreTypeFilterChange,
    storeTypeFilter,
    storeList,
    searchCount,
    setFilteredStores,
    setActiveStore,
    setCurrentPosition,
    currentPosition,
    setMapCenter,
    setZoom,
    setIsUserGeolocation
}) => {
    const { t } = useTranslation('common')
    const { openToast } = useUI()
    const [search, setSearch] = useState('')

    useEffect(() => {
        if (currentPosition) {
            filterStoresByRadius(storeList, currentPosition)
        }
    }, [currentPosition])

    const handleSearch = () => {
        setActiveStore(null)
        onSearch(search)
    }

    const handleRemoveSearch = () => {
        setCurrentPosition(null)
        setIsUserGeolocation(false)
        setSearch('')
        onSearch('')
    }

    const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') {
            handleSearch()
        }
    }

    const handleNearByStore = () => {
        if (navigator.geolocation) {
            navigator.geolocation.getCurrentPosition(
                (location) => {
                    const { coords: { latitude, longitude } = {} } = location

                    if (latitude !== undefined && longitude !== undefined) {
                        const position: google.maps.LatLngLiteral = {
                            lat: latitude,
                            lng: longitude
                        }
                        setCurrentPosition(position)
                        setZoom(17)
                        setIsUserGeolocation(true)
                        setActiveStore(null)
                    }
                },
                (error) => {
                    console.error('Error fetching location:', error)
                }
            )
        } else {
            console.error('Geolocation is not supported by this browser.')
        }
    }

    const filterStoresByRadius = (
        storeListData: Store[],
        currentPosition: google.maps.LatLngLiteral
    ): void => {
        let nearestCenter: google.maps.LatLngLiteral | null = null
        let nearestDistanceInKm = Number.POSITIVE_INFINITY

        const filteredStores = storeListData?.filter((store) => {
            const storeLat = Number.parseFloat(store.latitude)
            const storeLng = Number.parseFloat(store.longitude)

            const distance = getPreciseDistance(currentPosition, {
                latitude: storeLat,
                longitude: storeLng
            })
            const distanceInKm = distance / 1000

            if (distanceInKm <= 25) {
                if (distanceInKm < nearestDistanceInKm) {
                    nearestDistanceInKm = distanceInKm
                    nearestCenter = { lat: storeLat, lng: storeLng }
                }
                return true
            }

            return false
        })
        if (!filteredStores.length) {
            openToast(
                t(
                    'common||storeLocator|No OOKA locations found within a 25km radius of your search'
                ),
                ToastType.Danger
            )
            setMapCenter({ lat: currentPosition.lat, lng: currentPosition.lng })
        }

        if (nearestCenter) {
            setMapCenter(nearestCenter)
        }
        setFilteredStores(filteredStores || [])
    }

    const getStoreTypeFilters = () => {
        return [
            { value: 1, label: t('storeLocator|VENUE') },
            { value: 2, label: t('storeLocator|RETAIL') }
        ]
    }

    const renderStoreTypeFilters = (
        storeTypeFilterValue: number,
        storeTypeFilterLabel: string
    ) => {
        return (
            <li key={storeTypeFilterValue}>
                <input
                    id={`store-type-filter-${storeTypeFilterValue}`}
                    type="checkbox"
                    value={storeTypeFilterValue}
                    checked={storeTypeFilter.includes(storeTypeFilterValue)}
                    onChange={() =>
                        onStoreTypeFilterChange(storeTypeFilterValue)
                    }
                />
                <label htmlFor={`store-type-filter-${storeTypeFilterValue}`}>
                    {storeTypeFilterLabel}
                </label>
            </li>
        )
    }

    return (
        <div className={cn(s['search-container'])}>
            <h2 className={cn(s['heading'])}>
                {t('storeLocator|FIND OOKA NEAR YOU')}
            </h2>
            <div className={cn(s['search-box'])}>
                <input
                    id="searchQuery"
                    type="text"
                    placeholder={t('storeLocator|City or address')}
                    className={cn(s['form-input'])}
                    value={search}
                    onChange={(e) => {
                        setSearch(e.target.value)
                    }}
                    onKeyPress={handleKeyPress}
                />

                {search ? (
                    <div
                        className={cn(s['remove-search-btn'])}
                        onClick={handleRemoveSearch}
                        role="button"
                        onKeyDown={() => handleRemoveSearch()}
                        tabIndex={0}
                    >
                        <Cross />
                    </div>
                ) : (
                    <div
                        className={cn(s['remove-search-btn'])}
                        onClick={handleNearByStore}
                        role="button"
                        onKeyDown={() => handleNearByStore()}
                        tabIndex={0}
                    >
                        <CurrentLocation />
                    </div>
                )}

                <Button
                    type="submit"
                    className={cn(s['search-store'], 'btn-sm')}
                    onClick={handleSearch}
                    disabled={!search}
                >
                    {t('Search')}
                </Button>
            </div>
            <div className={cn(s['search-filter'])}>
                <ul>
                    {getStoreTypeFilters().map((store_type_filter) =>
                        renderStoreTypeFilters(
                            store_type_filter.value,
                            store_type_filter.label
                        )
                    )}
                </ul>
                {searchCount > 0 && (
                    <p className={cn(s['search-count'])}>
                        <span>{storeList?.length}</span>
                        <span>{t('storeLocator|results')}</span>
                    </p>
                )}
            </div>
        </div>
    )
}

export default StoreSearch
